import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import AdvancedSearchBase from '../';
import { AdvancedSearchShipmentContext } from './context';
import { PagingContext } from '../../contexts/paging-context';
import getFilterComponents from './filters';
import { id } from '../../filters/options/fulfillment-type';
import { fulfillmentTypeOptions } from '../../filters/options/fulfillment-type/options';
import filterViewActions from './filter-views';
import { FiltersContext } from '../../filters/context';

import { noop } from 'ui-components/default-props/noop';
import { FIRST_PAGE, PAGE_SIZE_250 } from '../../services/pagination-service';

export const shipmentsAdvancedSearch = 'shipmentsAdvancedSearch';

const ShipmentsAdvancedSearchContainer = ({ onSubmit, switchToAdvancedSearchView, isOnAdvancedSearch }) => {
  const { filters, resetFilters } = useContext(AdvancedSearchShipmentContext);
  const { page, pageSize } = useContext(PagingContext);
  const {
    buildApiFilters,
    replaceFiltersForSource,
    filters: contextFilters,
    replaceFilters: replaceAllContextFilters,
  } = useContext(FiltersContext);
  const { value: selectedfulfillmentTypeApiKey = {} } = filters.find((filter) => filter.id === id) || {};
  const selectedFulfillmentType = fulfillmentTypeOptions.find((opt) => opt.apiKey === selectedfulfillmentTypeApiKey);
  const filterView = filterViewActions[selectedFulfillmentType.id];
  const { orderBy, orderByDirection } = useSelector(filterView?.getOrderBy);

  const makeApiCall = useCallback(
    (pageNumber = FIRST_PAGE, pageCount = PAGE_SIZE_250) => {
      const apiCall = filterView.apiCall;
      if (!apiCall) {
        // in this case, the grid will control its own api call and thus its own filter building. Returning a promise for .then behaviors.
        return new Promise(noop, noop);
      }

      // if the filterview dictates a builder, use that. Otherwise, use the filter context entirely.
      const apiFilters = filterView.filterBuilder ? filterView.filterBuilder(filters) : buildApiFilters();

      return apiCall(apiFilters, pageNumber, pageCount, orderBy, orderByDirection);
    },
    [filters, orderBy, orderByDirection, filterView, buildApiFilters],
  );

  const updateFiltersContext = useCallback(
    (shouldReplaceAllFilters = false) => {
      const filterMap = filters.reduce((acc, filter) => {
        return [...acc, { ...filter, source: shipmentsAdvancedSearch }];
      }, []);
      if (shouldReplaceAllFilters) {
        replaceAllContextFilters(filterMap);
        return;
      }
      replaceFiltersForSource(shipmentsAdvancedSearch, filterMap);
    },
    [filters, replaceAllContextFilters, replaceFiltersForSource],
  );

  const onSubmitCallback = useCallback(() => {
    updateFiltersContext(!isOnAdvancedSearch);
    switchToAdvancedSearchView(selectedFulfillmentType.id);
    onSubmit(selectedFulfillmentType.id, makeApiCall, 1, pageSize);
  }, [makeApiCall, onSubmit, pageSize, switchToAdvancedSearchView, selectedFulfillmentType.id, updateFiltersContext, isOnAdvancedSearch]);

  useEffect(
    () => {
      if (isOnAdvancedSearch) {
        onSubmit(selectedFulfillmentType.id, makeApiCall, page, pageSize);
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, orderBy, orderByDirection, contextFilters],
  );

  useEffect(
    () => {
      return () => {
        replaceFiltersForSource(shipmentsAdvancedSearch);
      };
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const filterComponents = useMemo(() => getFilterComponents(selectedFulfillmentType.id), [selectedFulfillmentType]);

  return <AdvancedSearchBase filterComponents={filterComponents} onReset={resetFilters} onSubmit={onSubmitCallback} />;
};

ShipmentsAdvancedSearchContainer.propTypes = {
  switchToAdvancedSearchView: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isOnAdvancedSearch: PropTypes.bool.isRequired,
};

export default ShipmentsAdvancedSearchContainer;
